import { useCallback, useMemo, useState } from 'react'
import useMockableViewModel from '@/hooks/useMockableViewModel'
import { useAppSelector } from '@/hooks'
import { surveyResponsesCollection } from '@/reducers/surveyResponses'
import { SurveyResponse } from '@/types'
import { isNativeMobile } from '@/authenticated/hooks/useIsMobile'
import { isIOS } from '@/utils/dom/reactNativeDevice'
import useExternalNavigation from '@/app/hooks/useExternalNavigation'
import { remoteConfig } from '@/firebase'
import { optiwattConsts } from '@/optiwattConsts'
import { logEvent } from '@/logging'
import {
  getAnsweredAt,
  getDismissedAt,
  markAnswered,
  markDismissed,
} from './reviewBannerStorage'

export const MIN_ACCOUNT_AGE_DAYS = 30
/** How long a dismissal (the X) keeps the banner away */
export const DISMISS_QUIET_DAYS = 90
/** Feature feedback wins: stay quiet for this long after one is shown or answered */
export const FEATURE_FEEDBACK_QUIET_DAYS = 30

const DEFAULT_APP_STORE =
  'https://apps.apple.com/us/app/optiwatt/id1536047033?mt=8'
const DEFAULT_PLAY_STORE =
  'https://play.google.com/store/apps/details?id=com.getoptiwatt.optiwatt'

export type OpenModal = 'none' | 'up' | 'down'

export function daysSince(
  date: string | null | undefined,
  now: Date,
): number | null {
  if (!date) {
    return null
  }
  const parsed = new Date(date).getTime()
  if (Number.isNaN(parsed)) {
    return null
  }
  return (now.getTime() - parsed) / (1000 * 60 * 60 * 24)
}

/**
 * The most recent moment a feature-feedback banner was shown to or answered by
 * this person, anywhere in the app.
 *
 * Feature-feedback surveys stay pending forever by design, so people can answer
 * again as a feature changes. That makes "is one pending" useless as a signal:
 * it would silence the review banner permanently. We look at the dates instead.
 */
export function latestFeatureFeedbackActivity(
  responses: SurveyResponse[],
): string | null {
  const dates: string[] = []
  for (const response of responses) {
    if (response.survey.type !== 'FeatureFeedback') {
      continue
    }
    if (response.last_seen) {
      dates.push(String(response.last_seen))
    }
    if (response.completed) {
      dates.push(String(response.completed))
    }
    // FeatureFeedback appends one entry per submission, each stamped with responded_at
    const answers = response.response
    const entries = Array.isArray(answers) ? answers : answers ? [answers] : []
    for (const entry of entries) {
      const respondedAt = (entry as { responded_at?: unknown })?.responded_at
      if (typeof respondedAt === 'string') {
        dates.push(respondedAt)
      }
    }
  }
  if (dates.length === 0) {
    return null
  }
  return dates.reduce((latest, date) =>
    new Date(date).getTime() > new Date(latest).getTime() ? date : latest,
  )
}

export type EligibilityInput = {
  isNative: boolean
  onboardingPending: boolean
  dateJoined: string | null | undefined
  answeredAt: string | null
  dismissedAt: string | null
  lastFeatureFeedbackAt: string | null
  now: Date
}

export function isReviewBannerEligible(input: EligibilityInput): boolean {
  // The store review only makes sense inside the app
  if (!input.isNative) {
    return false
  }
  // Never compete with setup
  if (input.onboardingPending) {
    return false
  }
  const accountAge = daysSince(input.dateJoined, input.now)
  if (accountAge === null || accountAge < MIN_ACCOUNT_AGE_DAYS) {
    return false
  }
  // Answering either thumb retires the banner on this device
  if (input.answeredAt) {
    return false
  }
  const sinceDismissed = daysSince(input.dismissedAt, input.now)
  if (sinceDismissed !== null && sinceDismissed < DISMISS_QUIET_DAYS) {
    return false
  }
  const sinceFeatureFeedback = daysSince(input.lastFeatureFeedbackAt, input.now)
  if (
    sinceFeatureFeedback !== null &&
    sinceFeatureFeedback < FEATURE_FEEDBACK_QUIET_DAYS
  ) {
    return false
  }
  return true
}

export function toReviewUrl(storeUrl: string, ios: boolean): string {
  const param = ios ? 'action=write-review' : 'showAllReviews=true'
  return storeUrl.includes('?')
    ? `${storeUrl}&${param}`
    : `${storeUrl}?${param}`
}

function useViewModel() {
  const [openModal, setOpenModal] = useState<OpenModal>('none')
  const [closed, setClosed] = useState(false)
  const { externallyNavigate } = useExternalNavigation()

  const pendingResponses =
    surveyResponsesCollection.useFetch({ params: { pending_response: true } })
      ?.data ?? []
  const completedResponses =
    surveyResponsesCollection.useFetch({ params: { pending_response: false } })
      ?.data ?? []
  const dateJoined = useAppSelector((state) => state.user.user?.date_joined)

  const ios = isIOS()
  const eligible = useMemo(() => {
    const allResponses = [...pendingResponses, ...completedResponses]
    return isReviewBannerEligible({
      isNative: isNativeMobile(),
      onboardingPending: pendingResponses.some(
        (response) => response.survey.type === 'Onboarding',
      ),
      dateJoined,
      answeredAt: getAnsweredAt(),
      dismissedAt: getDismissedAt(),
      lastFeatureFeedbackAt: latestFeatureFeedbackActivity(allResponses),
      now: new Date(),
    })
  }, [pendingResponses, completedResponses, dateJoined])

  const storeUrl = ios
    ? remoteConfig?.getString('app_store_link') || DEFAULT_APP_STORE
    : remoteConfig?.getString('play_store_link') || DEFAULT_PLAY_STORE

  const onThumbsUp = useCallback(() => {
    logEvent('review_banner_thumbs_up')
    markAnswered()
    setOpenModal('up')
  }, [])

  const onThumbsDown = useCallback(() => {
    logEvent('review_banner_thumbs_down')
    markAnswered()
    setOpenModal('down')
  }, [])

  const onDismiss = useCallback(() => {
    logEvent('review_banner_dismissed')
    markDismissed()
    setClosed(true)
  }, [])

  const onCloseModal = useCallback(() => {
    setOpenModal('none')
    setClosed(true)
  }, [])

  const onOpenStore = useCallback(() => {
    logEvent('review_banner_store_opened')
    externallyNavigate(toReviewUrl(storeUrl, ios))
    setOpenModal('none')
    setClosed(true)
  }, [externallyNavigate, storeUrl, ios])

  const onEmailSupport = useCallback(() => {
    logEvent('review_banner_support_opened')
    window.location.href = `mailto:${optiwattConsts.contact.email}`
    setOpenModal('none')
    setClosed(true)
  }, [])

  return {
    show: eligible && !closed,
    openModal,
    storeLabel: ios ? 'Review on the App Store' : 'Review on Google Play',
    supportEmail: optiwattConsts.contact.email,
    onThumbsUp,
    onThumbsDown,
    onDismiss,
    onCloseModal,
    onOpenStore,
    onEmailSupport,
  }
}

function useMockViewModel() {
  const [openModal, setOpenModal] = useState<OpenModal>('none')
  return {
    show: true,
    openModal,
    storeLabel: 'Review on the App Store',
    supportEmail: optiwattConsts.contact.email,
    onThumbsUp: () => setOpenModal('up'),
    onThumbsDown: () => setOpenModal('down'),
    onDismiss: () => {},
    onCloseModal: () => setOpenModal('none'),
    onOpenStore: () => setOpenModal('none'),
    onEmailSupport: () => setOpenModal('none'),
  }
}

export default useMockableViewModel({ useViewModel, useMockViewModel })
